import firebase from "store/firebase";
import "firebase/firestore";

import { subscribe } from "./listen-change";

import { AnyAction, Dispatch } from "redux";
import type { RootState } from "store/reducer";
import { StoreError } from "store/errors";
import { eventFromDoc } from "../events/utils";
import { userFromDoc } from "./utils";
import { User } from "./reducer";
import { readResource } from "store/util/resource-read";

// New action types — list-level load keyed by event id, with the
// request-token staleness guard mirrored from id-resource.
export const ACTION_USERS_LIST_LOADING = "users/list_loading";
export const ACTION_USERS_LIST_LOADED = "users/list_loaded";
export const ACTION_USERS_LIST_ERRORED = "users/list_errored";

// Backwards-compatible aliases so legacy callers / tests that import these
// constants don't break.
export const ACTION_USERS_LOADING = ACTION_USERS_LIST_LOADING;
export const ACTION_USERS_LOAD = ACTION_USERS_LIST_LOADED;
export const ACTION_USERS_LOAD_ERROR = ACTION_USERS_LIST_ERRORED;

export default function load(eventId: string) {
  return async (
    dispatch: Dispatch<AnyAction>,
    getState: () => RootState
  ) => {
    const requestToken = (getState().users?.requestToken?.[eventId] ?? 0) + 1;
    dispatch({
      type: ACTION_USERS_LIST_LOADING,
      payload: { id: eventId, requestToken },
    });
    try {
      const { auth } = getState();
      if (!auth.user?.email) {
        throw new StoreError("No current user.");
      }
      const { related, items: mixedItems } = await readResource(
        getState,
        [ACTION_USERS_LIST_LOADING, eventId, auth.user.email],
        async () => {
          const doc = await firebase
            .firestore()
            .collection("events")
            .doc(eventId)
            .get();
          const item = eventFromDoc(doc);
          const related = item.related ?? [];
          const gets = related.map((id) =>
            firebase.firestore().collection("users").doc(id!).get()
          );
          const result = await Promise.all(gets);
          const items = result.map(userFromDoc);
          return { related, items: mixItems(related, items) };
        }
      );
      dispatch({
        type: ACTION_USERS_LIST_LOADED,
        payload: { id: eventId, items: mixedItems, requestToken },
      });
      // Subscribe AFTER the commit so the listener's first snapshot can't
      // race the bulk load.
      subscribe(related);
    } catch (err) {
      const error =
        err instanceof StoreError
          ? err
          : new StoreError(err instanceof Error ? err.message : String(err));
      dispatch({
        type: ACTION_USERS_LIST_ERRORED,
        payload: { id: eventId, error, requestToken },
      });
    }
  };
}

function mixItems(related: (string | null)[], items: User[] | null): User[] {
  return related.map((id) => {
    const foundUser = items?.find((item) => item.id === id);
    return { id: id ?? "", email: id ?? "", ...foundUser };
  });
}
